/** @file App: the editor root: store and client providers, hash route, the one key listener, login gate. */
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { Pose3DClient } from '@zilo/pose3d-api/client/createClient';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import { EditorStoreContext } from '../state/editorStoreContext';
import type { EditorStore } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { apiAdapter } from '../store/apiAdapter';
import type { EditorApi } from '../store/editorApi';
import { Spinner } from '../ui-kit/Spinner';
import { ApiContext } from './apiContext';
import { applyTheme } from './applyTheme';
import { consumeResetFlag } from './consumeResetFlag';
import { EDITOR_SETTINGS } from './editorSettings';
import { installShortcuts } from './installShortcuts';
import { LoginScreen } from './LoginScreen';
import { PLATFORM } from './platform';
import { useHashRoute } from './router/useHashRoute';
import { setLiveChords } from './setLiveChords';
import { Shell } from './Shell';
import { shellBindings } from './shellBindings';
import { shellMenuBindings } from './shellMenuBindings';
import { transportBindings } from './transportBindings';
import { useContextMenuGuard } from './useContextMenuGuard';
import { useLastRoute } from './useLastRoute';
import { usePuppetRoute } from './usePuppetRoute';
import { useSession } from './useSession';

/**
 * The store, the api client (null = no server: the memory store dev page), an `api` that replaces the adapted client
 * (dev pages over a fake server) and the panels' binding tables.
 */
export type AppProps = { store: EditorStore; client: Pose3DClient | null; api?: EditorApi; bindingTables?: Binding[][] };

const NO_TABLES: Binding[][] = [];

/** Inside the providers: routing, the puppet the route names, the key listener, stored theme and grid, and the login gate. */
function Root({ store, api, bindingTables = NO_TABLES }: Omit<AppProps, 'client' | 'api'> & { api: EditorApi | null }): ReactElement {
  useState(consumeResetFlag);
  const [overrides, setOverrides] = useState<Binding[]>(EDITOR_SETTINGS.shortcuts.load);
  const tables = useMemo(() => [shellBindings, shellMenuBindings, transportBindings, ...bindingTables], [bindingTables]);
  useEffect(() => { setLiveChords(tables, overrides); }, [tables, overrides]);
  useContextMenuGuard();
  useHashRoute(store);
  useLastRoute(store);
  useEffect(() => {
    applyTheme(EDITOR_SETTINGS.theme.load());
    store.getState().setCamera({ grid: EDITOR_SETTINGS.pixelGrid.load() });
  }, [store]);
  useSession(store, api);
  usePuppetRoute(store, api !== null);
  useEffect(() => installShortcuts(store, { platform: PLATFORM, overrides }), [store, overrides]);
  const status = useEditor((s) => s.session.status);
  const onOverrides = (next: Binding[]): void => {
    EDITOR_SETTINGS.shortcuts.save(next.length === 0 ? null : next);
    setOverrides(next);
  };
  if (status === 'unknown') return <div className="p3-login"><Spinner label="Connecting" /></div>;
  if (status === 'anonymous' && api !== null) return <LoginScreen api={api} />;
  return <Shell bindingTables={tables} overrides={overrides} onOverrides={onOverrides} />;
}

/** Provides `store` and the client (as `EditorApi`, through `apiAdapter`) to everything below and renders the shell. */
export function App({ client, api: given, ...props }: AppProps): ReactElement {
  const api = useMemo(() => given ?? (client === null ? null : apiAdapter(client)), [client, given]);
  return (
    <EditorStoreContext.Provider value={props.store}>
      <ApiContext.Provider value={api}>
        <Root {...props} api={api} />
      </ApiContext.Provider>
    </EditorStoreContext.Provider>
  );
}
